import { captureException } from "@sentry/react";
import {
  buildAfterMarkdown,
  buildBeforeMarkdown,
  computeContentDiff,
  escapeHtmlContent,
} from "@src/utils/diffUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import type { PluggableList } from "unified";

import { NoteSection, sectionConfig } from "./DSNoteInput";
import { DSDiffActions } from "./DSNoteInputDiffActions";
import { MarkdownContent } from "../atoms/MarkdownContent"; // Handles newlines via white-space: pre-wrap
import { DSRichTextToolbar } from "../molecules/DSRichTextToolbar";

export interface DSDiffViewProps {
  /** The AI-generated "after" content */
  afterContent: string | null;
  /** The original "before" content */
  beforeContent: string;
  /** The section type - used to look up styling automatically */
  section: NoteSection;
  /** Optional label override (defaults to section's label) */
  label?: string;
  /** Whether to use compact layout (stacked instead of side-by-side) */
  isCompact?: boolean;
  /** Whether content is still loading/streaming */
  isLoading?: boolean;
  /** Callback when Accept button is clicked */
  onAccept: () => void;
  /** Callback when Reject button is clicked */
  onReject: () => void;
  /** Test ID for automated testing */
  dataTestId: string;
  /** Whether to render plain text instead of rich text markdown */
  plainText?: boolean;
}

/** Shared markdown styling classes for list, bold, italic rendering */
const markdownClasses =
  "[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-1 [&_strong]:font-bold [&_em]:italic";

/** Diff highlight classes for <ins> tags (additions - green) */
const insClasses =
  "[&_ins]:bg-success-subtle [&_ins]:text-success [&_ins]:no-underline [&_ins]:rounded-sm [&_ins]:px-0.5";

/** Diff highlight classes for <del> tags (deletions - red) */
const delClasses =
  "[&_del]:bg-danger-subtle [&_del]:text-danger [&_del]:rounded-sm [&_del]:px-0.5";

/** Sanitization schema: default safe tags + diff highlights + rich text formatting */
const diffSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    "ins",
    "del",
    "u",
    "s",
    "sub",
    "sup",
    "mark",
  ],
};

const DIFF_REMARK_PLUGINS = [remarkGfm];

// rehype-raw + rehype-sanitize: diff highlights are injected as raw <ins>/<del>
// HTML strings into markdown. rehype-raw parses them so react-markdown renders
// them as real elements; rehype-sanitize strips anything dangerous. An alternative
// (rendering each diff segment as a separate React element) would eliminate these
// deps but requires a formatting state machine to handle markdown syntax that
// spans diff boundaries (e.g. **bold across added|unchanged** segments).
//
// IMPORTANT — Newline rendering contract:
// Newlines in note content render via: react-markdown emits \n text nodes for
// soft breaks → ds-markdown's `white-space: pre-wrap` displays them visually.
// rehype-raw breaks this by normalizing \n text nodes during HTML re-parsing.
// Therefore: use MarkdownContent (no rehype-raw) for the no-diff path, and only
// use the rehype pipeline below for the diff path where raw <ins>/<del> tags
// require it. Do NOT consolidate both paths into a single <Markdown> with
// rehype-raw — it will silently eat newlines.
const DIFF_REHYPE_PLUGINS: PluggableList = [
  rehypeRaw,
  [rehypeSanitize, diffSanitizeSchema],
];

function getPanelStyle(isCompact: boolean, plainText: boolean) {
  return {
    minHeight: isCompact ? "80px" : "120px",
    maxHeight: isCompact ? "200px" : "400px",
    ...(plainText && { whiteSpace: "pre-wrap" as const }),
  };
}

/**
 * Render pre-escaped HTML containing only <ins>/<del> diff markers.
 * All user/AI text must be escaped via escapeHtmlContent before reaching here,
 * so only our injected diff tags remain as actual HTML.
 */
const PlainTextDiff = ({ escapedHtml }: { escapedHtml: string }) => (
  <span dangerouslySetInnerHTML={{ __html: escapedHtml }} />
);

/**
 * Side-by-side diff view for comparing AI-generated content with original content.
 * Shows "New" content on the left and "Original" content on the right in default mode.
 * In compact mode, shows New on top and Original on bottom.
 * Word-level diff highlights show additions (green) and deletions (red) within each panel.
 */
export const DSDiffView = ({
  afterContent,
  beforeContent,
  section,
  label,
  isCompact = false,
  isLoading = false,
  onAccept,
  onReject,
  dataTestId,
  plainText = false,
}: DSDiffViewProps) => {
  const config = sectionConfig[section];
  const displayLabel = label || config.label;
  const { releaseWebEnableVisualDiffs } = useFlags();
  const layoutClass = isCompact
    ? "flex flex-col gap-sm"
    : "flex flex-row gap-md h-full";

  // Skip diff computation while streaming — no benefit to highlighting partial content,
  // and avoids running diffWords + rehype pipeline on every streaming chunk.
  // Also skip when visual diffs feature flag is disabled.
  const { wordStatuses, diffError } = useMemo(() => {
    if (!releaseWebEnableVisualDiffs || !afterContent || isLoading)
      return { wordStatuses: null, diffError: false };
    try {
      const statuses = computeContentDiff(
        beforeContent || "",
        afterContent,
      );
      return {
        wordStatuses: statuses.length > 0 ? statuses : null,
        diffError: false,
      };
    } catch (err) {
      captureException(err);
      return { wordStatuses: null, diffError: true };
    }
  }, [
    releaseWebEnableVisualDiffs,
    beforeContent,
    afterContent,
    isLoading,
  ]);

  const { afterMarkdown, beforeMarkdownHighlighted } = useMemo(() => {
    if (!wordStatuses) {
      // No diff highlights needed — plainText path needs escaping,
      // rich text path uses MarkdownContent directly (values unused).
      return {
        afterMarkdown: plainText
          ? escapeHtmlContent(afterContent || "")
          : "",
        beforeMarkdownHighlighted: plainText
          ? escapeHtmlContent(beforeContent)
          : "",
      };
    }
    return {
      afterMarkdown: buildAfterMarkdown(
        afterContent!,
        wordStatuses,
        plainText,
      ),
      beforeMarkdownHighlighted: buildBeforeMarkdown(
        beforeContent,
        wordStatuses,
        plainText,
      ),
    };
  }, [wordStatuses, beforeContent, afterContent, plainText]);

  const panelStyle = getPanelStyle(isCompact, plainText);

  return (
    <div
      className="flex flex-row items-start gap-sm"
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      {/* Letter badge for compact mode */}
      {isCompact && config.letter && (
        <span
          className={`text-xl font-bold shrink-0 ${config.textClass}`}
          aria-label={displayLabel}
        >
          {config.letter}
        </span>
      )}

      <div className="flex-1 min-w-0 flex flex-col gap-xs">
        {/* Toolbar header with label and Accept/Reject buttons */}
        <DSRichTextToolbar
          textareaTitle={displayLabel}
          bgClass={config.toolbarBgClass}
          borderClass={
            config.toolbarBorderClass || "border-secondary"
          }
          dataTestId={`${dataTestId}-toolbar`}
        >
          <DSDiffActions
            onAccept={onAccept}
            onReject={onReject}
            disabled={isLoading}
            dataTestId={`${dataTestId}-actions`}
          />
        </DSRichTextToolbar>

        <div
          className={`${layoutClass} ${config.bgClass} border-2 border-brand rounded-standard p-md`}
        >
          <div className={isCompact ? "" : "flex-1 min-w-0"}>
            <h4 className="text-sm font-semibold text-primary mb-xs">
              New
            </h4>
            {diffError && (
              <div
                className="text-xs text-warning mb-xs"
                data-testid={`${dataTestId}-diff-error`}
              >
                Unable to highlight changes. Please try again later.
              </div>
            )}
            {isLoading && !afterContent ? (
              <div
                className="p-sm text-sm text-secondary italic"
                style={panelStyle}
                data-testid={`${dataTestId}-after`}
              >
                Generating content...
              </div>
            ) : afterContent ? (
              <div
                className={`p-sm text-sm text-primary overflow-auto ${plainText ? "" : `ds-markdown ${markdownClasses}`} ${wordStatuses ? insClasses : ""}`}
                style={panelStyle}
                data-testid={`${dataTestId}-after`}
              >
                {plainText ? (
                  <PlainTextDiff escapedHtml={afterMarkdown} />
                ) : wordStatuses ? (
                  <Markdown
                    remarkPlugins={DIFF_REMARK_PLUGINS}
                    rehypePlugins={DIFF_REHYPE_PLUGINS}
                  >
                    {afterMarkdown}
                  </Markdown>
                ) : (
                  <MarkdownContent content={afterContent || ""} />
                )}
              </div>
            ) : (
              <div
                className="p-sm text-sm text-secondary italic"
                style={panelStyle}
                data-testid={`${dataTestId}-after`}
              >
                No content generated yet
              </div>
            )}
          </div>

          <div
            className={
              isCompact
                ? "border-t border-secondary"
                : "border-l border-secondary"
            }
          />

          <div className={isCompact ? "" : "flex-1 min-w-0"}>
            <h4 className="text-sm font-semibold text-secondary mb-xs">
              Original
            </h4>
            {beforeContent ? (
              <div
                className={`p-sm text-sm text-secondary overflow-auto italic opacity-75 ${plainText ? "" : `ds-markdown ${markdownClasses}`} ${wordStatuses ? delClasses : ""}`}
                style={panelStyle}
                data-testid={`${dataTestId}-before`}
              >
                {plainText ? (
                  <PlainTextDiff
                    escapedHtml={beforeMarkdownHighlighted}
                  />
                ) : wordStatuses ? (
                  <Markdown
                    remarkPlugins={DIFF_REMARK_PLUGINS}
                    rehypePlugins={DIFF_REHYPE_PLUGINS}
                  >
                    {beforeMarkdownHighlighted}
                  </Markdown>
                ) : (
                  <MarkdownContent content={beforeContent} />
                )}
              </div>
            ) : (
              <div
                className="p-sm text-sm text-placeholder"
                style={panelStyle}
                data-testid={`${dataTestId}-before`}
              >
                No original content
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
